import { useCallback } from 'react';

import { Box, IconButton, Tooltip } from '@mui/material';

import MapPinIcon from '@/components/Icons/MapPinIcon';
import MapPinnedIcon from '@/components/Icons/MapPinnedIcon';
import { MAX_PINNED_IDS } from '@/constants';
import useUser from '@/hooks/useUser';
import { useSnackbar } from 'notistack';
import { useRecoilState } from 'recoil';

import { pinnedIdsState } from '../atoms';
import { WorkspaceSections } from '../types';
import { sortPinnedIds } from '../utils';
import { useWorkspaceChainsQuery } from './__generated__/queries.generated';

const POLL_INTERVAL = 15000;

function useChains(poll: boolean = false) {
  const { data, loading } = useWorkspaceChainsQuery({
    fetchPolicy: 'cache-first',
    pollInterval: poll ? POLL_INTERVAL : null,
  });

  return { chains: data?.chains || [], loading };
}

function usePinnedAPI() {
  const user = useUser();
  const { enqueueSnackbar } = useSnackbar();
  const [pinnedIds, setPinnedIds] = useRecoilState(
    pinnedIdsState(`${WorkspaceSections.Chains}-${user.id}`),
  );
  const { chains, loading } = useChains();

  const pinnedChains = chains
    .filter((chain) => pinnedIds.includes(chain.id))
    .sort((a, b) => sortPinnedIds(pinnedIds, a.id, b.id));

  const togglePinEntity = useCallback(
    (entityId: string, entityName: string) => {
      setPinnedIds((prev) => {
        if (prev.includes(entityId)) {
          return prev.filter((id) => id !== entityId);
        }
        enqueueSnackbar(`Pinned ${entityName} to the sidebar`, { variant: 'success' });
        return [entityId, ...prev];
      });
    },
    [setPinnedIds, enqueueSnackbar],
  );

  const canPinMoreChains = pinnedChains.length < MAX_PINNED_IDS;

  function stopPropagation(ev: React.MouseEvent) {
    ev.preventDefault();
    ev.stopPropagation();
  }

  function getPinActionItem(entityId: string, chainName: string) {
    const isPinned = pinnedIds.includes(entityId);
    const isPinDisabled = !canPinMoreChains && !isPinned;

    return (
      <Tooltip
        title={
          isPinDisabled
            ? `Limit ${MAX_PINNED_IDS} pins`
            : isPinned
            ? 'Unpin from sidebar'
            : 'Pin to sidebar'
        }
      >
        <Box>
          <IconButton
            onClick={(e) => {
              stopPropagation(e);
              togglePinEntity(entityId, chainName);
            }}
            data-cy="pin-entity-btn"
            disabled={isPinDisabled}
          >
            {isPinned ? <MapPinnedIcon color="inherit" /> : <MapPinIcon color="secondary" />}
          </IconButton>
        </Box>
      </Tooltip>
    );
  }

  return { pinnedIds, togglePinEntity, canPinMoreChains, getPinActionItem, loading, pinnedChains };
}

export { useChains, usePinnedAPI };
